import { createDomain } from 'effector';
import { forward } from 'effector/effector.umd';
import { createGate } from 'effector-react';
import type { GeneralID } from '@types';
import { MS_PER_TICK, PX_PER_SEC } from '@modules/autoRotate/constants';
import type { StandardSubtitle } from '@utils/subtitleFormat';
import { $sceneMapApi } from './dataStores';

const domain = createDomain('autoRotate');

const reset = domain.event();

const setCurrentLeft = domain.event<number>();
const tick = domain.event();
export const $rotateCurrentLeft = domain
  .store<number>(0)
  .on(setCurrentLeft, (state, payload) => {
    if (payload < 0) return 0;
    return payload;
  })
  .on(tick, (state) => state + (MS_PER_TICK / 1000) * PX_PER_SEC)
  .reset(reset);

export type RotateEditAction = {
  type: 'moveLine' | 'moveBlock' | 'resizeRight' | 'resizeLeft';
  index: number;
  mouseLeft: number;
  scrollBarLeft: number;
  blockLeft: number;
};

const setAction = domain.event<RotateEditAction | null>();
export const $rotateEditAction = domain
  .store<RotateEditAction | null>(null)
  .reset(reset);
forward({
  from: setAction,
  to: $rotateEditAction,
});

const moveBlock = domain.event<{
  index: number;
  targetLeft: number;
  sceneId: GeneralID;
}>();
moveBlock.watch(({ sceneId, index, targetLeft }) => {
  $sceneMapApi.updateScene({
    id: sceneId,
    updater: (draft) => {
      if (!draft.config.autoRotate) return;
      const list = draft.config.autoRotate.tourData ?? [];
      const item = list[index];
      if (!item) return;
      const minStartAt = list[index - 1]
        ? list[index - 1].startAt + list[index - 1].duration
        : 0;
      const targetTime = Math.max(minStartAt, targetLeft / PX_PER_SEC);
      const diff = targetTime - item.startAt;
      draft.config.autoRotate.tourData = list.map((x, i) => {
        if (i < index) return x;
        return {
          ...x,
          startAt: x.startAt + diff,
        };
      });
    },
  });
});

const resizeRight = domain.event<{
  index: number;
  targetLeft: number;
  sceneId: GeneralID;
}>();
resizeRight.watch(({ sceneId, index, targetLeft }) => {
  $sceneMapApi.updateScene({
    id: sceneId,
    updater: (draft) => {
      if (!draft.config.autoRotate) return;
      const list = draft.config.autoRotate.tourData ?? [];
      const item = list[index];
      if (!item) return;
      const minStartAt = item.startAt + 1;
      const targetTime = Math.max(minStartAt, targetLeft / PX_PER_SEC);
      const nextItem = list[index + 1];
      const diff = nextItem ? Math.max(targetTime - nextItem.startAt, 0) : 0;
      draft.config.autoRotate.tourData = list.map((x, i) => {
        if (i < index) return x;
        if (i === index) {
          return {
            ...x,
            duration: targetTime - x.startAt,
          };
        }
        return {
          ...x,
          startAt: x.startAt + diff,
        };
      });
    },
  });
});

const resizeLeft = domain.event<{
  index: number;
  targetLeft: number;
  sceneId: GeneralID;
}>();
resizeLeft.watch(({ sceneId, index, targetLeft }) => {
  $sceneMapApi.updateScene({
    id: sceneId,
    updater: (draft) => {
      if (!draft.config.autoRotate) return;
      const list = draft.config.autoRotate.tourData ?? [];
      const item = list[index];
      if (!item) return;
      const right = item.startAt + item.duration;
      const minStartAt = list[index - 1]
        ? list[index - 1].startAt + list[index - 1].duration
        : 0;
      const maxStartAt = right - 1;
      const targetTime = Math.max(
        Math.min(maxStartAt, targetLeft / PX_PER_SEC),
        minStartAt
      );
      const duration = right - targetTime;
      item.startAt = targetTime;
      item.duration = duration;
    },
  });
});

const play = domain.event();
const stop = domain.event();

export const $rotateIsPlaying = domain
  .store<boolean>(false)
  .on(play, () => true)
  .on(stop, () => false)
  .reset(reset);

export const $rotateEditApi = {
  setCurrentLeft,
  tick,
  setAction,
  moveBlock,
  resizeRight,
  resizeLeft,
  reset,
  play,
  stop,
};

export const $GateRotateAudioLength = createGate({ defaultState: 0, domain });
export const $GateRotateSubtitleLength = createGate({
  defaultState: 0,
  domain,
});
export const $GateRotateEditMode = createGate({ defaultState: false, domain });

export const $GateRotateItemEditing = createGate<{
  isChanged: boolean;
  id: string;
} | null>({ defaultState: null });

export const $GateRotateSubtitleList = createGate<StandardSubtitle[]>({
  defaultState: [],
  domain,
});
